import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe, PaymentRequestEvent, Stripe, StripeElementLocale } from '@stripe/stripe-js';
import Router from 'next/router';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import type { Impacter } from '../../../utils/api/impacter';
import { FlowType, PaymentType, Provider, SubscriptionFlowType } from '../../../types/PaymentTypes';
import {
  EVENT_NAMES,
  sendEvent,
  sendPaymentIntentFailedEvent,
  sendPaymentIntentSuccessEvent,
  setUserProperties,
} from '../../../utils/analytics/analytics';
import {
  createPaymentRequestForStripe,
  getPaymentRequestByExternalId,
} from '../../../utils/api/paymentRequests';
import { createStripeSubscription } from '../../../utils/api/subscription';
import CheckoutContext from '../../../utils/checkoutContext';
import { microcopySet, useMicrocopy } from '../../../utils/contentfulMicrocopy';
import { stripeFonts } from '../../../utils/fonts';
import { useImpactersData } from '../../../utils/hooks/useImpactersDataArray';
import useLocale from '../../../utils/hooks/useLocale';
import usePathname from '../../../utils/hooks/usePathname';
import { usePartnerParams } from '../../../utils/partnerParams';
import { handlePaymentSetupIntent } from '../../../utils/stripeHelpers';
import { colors, spacing } from '../../../utils/styleguide';
import { sendGTMEvent } from '../../../utils/tracking/gtm/sendGTMEvent';
import { captureException } from '../../../utils/tracking/sentry/sentry';
import GhostButton from '../buttons/GhostButton';
import PrimaryButton, { ButtonProps } from '../buttons/PrimaryButton';
import Checkbox from '../forms/Checkbox';
import Spinner from '../Spinner';
import Typography, { TypographyVariants } from '../text/Typography';
import PropsComponent from '../types/PropsComponent';
import { StripePaymentCategory } from './PaymentCategory';
import StripePaymentMethodForm from './stripe/StripePaymentMethodForm';
import StripePaymentRequestButton from './stripe/StripePaymentRequestButton';
import Turnstile, { TurnstileRef } from './Turnstile';
import TurnstileErrorBoundary from './TurnstileErrorBoundary';

const CenterWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ErrorBlock = styled.div`
  background-color: ${colors.lightRed};
  padding: ${spacing[1]}px ${spacing[2]}px;
`;

type StripeCheckoutProps = {
  publicKey?: string;
  stripeAccountId?: string | null;
  grantmakerId?: string;
  currencyCode: string;
  amount?: number;
  onSuccess?: (obj: { subscriptionId?: string; paymentRequestId?: string }) => void;
  paymentFlowId?: string;
  initiator?: string;
  campaign?: string;
  contentfulLandingPageId: string;
  projectId?: number;
  impacters?: Pick<Impacter, 'id' | 'name' | 'externalId'>[];
  category?: StripePaymentCategory;
  variant?: 'verbose' | 'dense';
  buttonLabel?: string;
  enableStripePaymentRequestButton?: boolean;
  locale?: StripeElementLocale;
  paymentType?: PaymentType;
  skipAmountValidation?: boolean;
  isVisible?: boolean;
  colorTheme?: 'default' | 'dark' | 'ppc';
  DonationCheckoutButton?: DonationButtonType;
};

export type DonationButtonType = PropsComponent<
  Pick<ButtonProps, 'label' | 'onClick' | 'type' | 'disabled' | 'loading'>
>;

type BillingDetails = { email: string; firstName?: string; lastName?: string };

export default function StripeCheckout({
  publicKey,
  stripeAccountId,
  grantmakerId,
  currencyCode,
  amount,
  onSuccess,
  paymentFlowId,
  contentfulLandingPageId,
  initiator,
  campaign,
  projectId,
  impacters,
  category,
  variant = 'verbose',
  buttonLabel,
  enableStripePaymentRequestButton,
  locale = 'en',
  paymentType = PaymentType.ONE_TIME,
  skipAmountValidation,
  isVisible,
  colorTheme,
  DonationCheckoutButton,
}: StripeCheckoutProps) {
  const t = useMicrocopy();
  const routerLocale = useLocale();
  const { affiliateName } = useContext(CheckoutContext);
  const [stripe, setStripe] = useState<Stripe | null>();
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [paymentRequestEnabled, setPaymentRequestEnabled] = useState<boolean | undefined>();
  const [emailOptIn, setEmailOptIn] = useState(false);
  const turnstile = useRef<TurnstileRef>(null);
  const turnstileToken = useRef<string>();
  const [availableWallets, setAvailableWallets] = useState<
    PaymentRequestEvent['walletName'][] | string[]
  >(['card']);
  const { impacterIds, impacterNames, impacterPairs } = useImpactersData(impacters);
  const path = usePathname();
  const { donationProgramId, partnerId } = usePartnerParams();

  useEffect(() => {
    if (publicKey) {
      const load = async () => {
        const stripe = stripeAccountId
          ? await loadStripe(publicKey, { stripeAccount: stripeAccountId })
          : await loadStripe(publicKey);
        setStripe(stripe);
      };
      load();
    }
  }, [publicKey, stripeAccountId]);

  const getCommonSupportEventProperties = () => ({
    amount,
    'currency code': currencyCode,
    'payment provider': 'stripe' as Provider,
    'support type':
      paymentType === PaymentType.ONE_TIME ? PaymentType.ONE_TIME : PaymentType.SUBSCRIPTION,
    'flow type':
      paymentType === PaymentType.ONE_TIME
        ? FlowType.ONE_TIME
        : SubscriptionFlowType.createSubscription,
    path,
    'payment flow id': paymentFlowId,
  });

  const getImpacterAndCategoryEventProperties = () => ({
    'category id': category?.id,
    'impacter ids': impacterIds,
    'impacter names': impacterNames,
  });

  const sendAvailableWalletsEvent = () => {
    sendEvent({
      name: EVENT_NAMES.supportAvailableWallets,
      properties: {
        ...getCommonSupportEventProperties(),
        'available wallets': availableWallets,
      },
    });
  };

  useEffect(() => {
    if (!enableStripePaymentRequestButton) {
      sendAvailableWalletsEvent();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (paymentRequestEnabled === undefined) {
      return;
    } else {
      sendAvailableWalletsEvent();
    }
  }, [paymentRequestEnabled]); // eslint-disable-line react-hooks/exhaustive-deps

  const emailOptInLabel = t(microcopySet.PAYMENT_OPTION, 'emailOptInLabel');

  const handleUpdateAvailableWallets = useCallback(
    (wallet: string) => {
      if (!availableWallets.includes(wallet)) {
        setAvailableWallets([...availableWallets, wallet]);
      }
    },
    [availableWallets],
  );

  if (!stripe) {
    return <Spinner />;
  }

  const createPaymentRequest = async (paymentMethod: string, billingDetails: BillingDetails) => {
    if (!amount) return;

    const response = await createPaymentRequestForStripe({
      amount: amount,
      contentfulLandingPageId,
      currencyCode: currencyCode,
      projectIds: projectId ? [projectId] : undefined,
      impacterIds: impacterIds,
      paymentMethod,
      ...billingDetails,
      initiator: initiator,
      affiliateName,
      categoryIds: category ? [category.id] : undefined,
      language: routerLocale,
      emailOptIn,
      skipAmountValidation,
      verificationToken: turnstileToken.current,
      donationProgramId,
      partnerId,
      grantmakerId,
    });

    if (response.requiresAction && stripe) {
      const result = await stripe.confirmCardPayment(response.paymentIntentClientSecret);
      if (result.error) {
        throw result.error;
      } else {
        return response;
      }
    } else {
      return response;
    }
  };

  const logSuccess = ({
    type,
    userId,
  }: {
    type: PaymentRequestEvent['walletName'];
    userId?: number;
  }) => {
    if (!userId) {
      captureException(new Error('User did a payment but we did not get any user id'));
    } else {
      // Even if the user is not logged in, they now have an account we can link to
      setUserProperties({ id: userId });
    }

    sendPaymentIntentSuccessEvent({
      amount,
      currencyCode,
      paymentProvider: 'stripe',
      supportType: paymentType === PaymentType.ONE_TIME ? 'one time' : 'monthly',
      path,
      flowType:
        paymentType === PaymentType.ONE_TIME
          ? FlowType.ONE_TIME
          : SubscriptionFlowType.createSubscription,
      selectedWallet: type,
    });

    sendEvent({
      name: EVENT_NAMES.supportSuccessful,
      properties: {
        ...getImpacterAndCategoryEventProperties(),
        ...getCommonSupportEventProperties(),
        'payment method': type,
        initiator,
        campaign,
        paymentRequestEnabled,
      },
    });

    sendGTMEvent('purchase', {
      currencyCode,
      paymentFlowId,
      amount,
      paymentType: paymentType,
      items: category ? [category] : impacterPairs,
      donationItemType: category ? 'Category' : impacterPairs.length ? 'Impacter' : undefined,
    });
  };

  const setupSubscription = async (
    paymentMethodId: string,
    userInformation: { email: string; firstName?: string; lastName?: string },
    emailOptIn: boolean,
  ) => {
    if (!amount || (!impacters && !category)) {
      throw new Error('missing parameters');
    }
    const { subscription, providerResponse, user } = await createStripeSubscription({
      affiliateName,
      amount,
      categoryIds: category ? [category.id] : undefined,
      currencyCode,
      externalImpacterIds: impacters?.map((impacter) => impacter.externalId),
      paymentMethodId,
      language: routerLocale,
      contentfulLandingPageId,
      verificationToken: turnstileToken.current,
      user: {
        emailOptIn,
        ...userInformation,
      },
    });

    const pendingSetupIntent = providerResponse?.pendingSetupIntent;
    if (pendingSetupIntent) {
      await handlePaymentSetupIntent({
        stripe,
        status: pendingSetupIntent.status,
        setupIntentClientSecret: pendingSetupIntent.client_secret,
      });
    }
    return { subscription, user };
  };

  async function handlePaymentMethod(
    paymentMethod: string,
    type: PaymentRequestEvent['walletName'],
    billingDetails: { email: string; firstName?: string; lastName?: string },
  ) {
    try {
      sendEvent({
        name: EVENT_NAMES.supportInitiatePayment,
        properties: {
          ...getCommonSupportEventProperties(),
          'selected wallet': type,
          'available wallets': availableWallets,
        },
      });

      sendGTMEvent('checkout', {
        currencyCode,
        amount,
        paymentType: paymentType,
        items: category ? [category] : impacterPairs,
        donationItemType: category ? 'Category' : impacterPairs.length ? 'Impacter' : undefined,
      });

      trackConfirm(type);
      setIsLoading(true);
      if (paymentType === 'subscription') {
        const { subscription, user } = await setupSubscription(
          paymentMethod,
          billingDetails,
          emailOptIn,
        );
        if (onSuccess) {
          logSuccess({ type, userId: user.id });
          onSuccess({ subscriptionId: subscription.externalId });
        }
      } else {
        const paymentRequest = await createPaymentRequest(paymentMethod, billingDetails);
        if (!paymentRequest) throw Error('Got no paymentRequest ');
        await waitForSuccess(paymentRequest.id, type);
      }
    } catch (error) {
      // @ts-expect-error FIXME: handle error types explicitly for improved error handling
      if (error.errorCode === 'SubscriptionExists') {
        setErrorMessage(t(microcopySet.PAYMENT_OPTION, 'subscriptionUserExists'));
      } else {
        // @ts-expect-error FIXME: handle error types explicitly for improved error handling
        setErrorMessage(error.customerMessage);
        turnstile.current?.reset();
      }
      setIsLoading(false);

      sendPaymentIntentFailedEvent({
        amount,
        currencyCode,
        paymentProvider: 'stripe',
        supportType: paymentType === PaymentType.ONE_TIME ? 'one time' : 'monthly',
        path,
        flowType:
          paymentType === PaymentType.ONE_TIME
            ? FlowType.ONE_TIME
            : SubscriptionFlowType.createSubscription,
        selectedWallet: type,
        // @ts-expect-error FIXME: handle error types explicitly for improved error handling
        error: error.customerMessage,
      });

      sendEvent({
        name: EVENT_NAMES.supportFailed,
        properties: {
          ...getImpacterAndCategoryEventProperties(),
          ...getCommonSupportEventProperties(),
          'payment method': type,
          error: error,
          paymentRequestEnabled,
        },
      });
    }
  }

  const waitForSuccess = async (
    paymentRequestId: string,
    type: PaymentRequestEvent['walletName'],
  ) => {
    const paymentRequest = await getPaymentRequestByExternalId(paymentRequestId);

    if (paymentRequest.status === 'SUCCEEDED') {
      logSuccess({ type, userId: paymentRequest.userId });

      if (onSuccess) {
        onSuccess({ paymentRequestId });
      }
    } else {
      return new Promise<void>((resolve) =>
        setTimeout(async () => {
          await waitForSuccess(paymentRequestId, type);
          resolve();
        }, 1000),
      );
    }
  };

  const trackConfirm = (type: PaymentRequestEvent['walletName']) => {
    sendEvent({
      name: EVENT_NAMES.supportClickConfirm,
      properties: {
        ...getImpacterAndCategoryEventProperties(),
        ...getCommonSupportEventProperties(),
        'payment method': type,
        initiator,
        campaign,
        paymentRequestEnabled,
      },
    });
  };

  return (
    <Elements options={{ fonts: stripeFonts, locale }} stripe={stripe}>
      {enableStripePaymentRequestButton ? (
        <>
          <StripePaymentRequestButton
            currencyCode={currencyCode}
            amount={amount}
            setPaymentRequestEnabled={setPaymentRequestEnabled}
            onPaymentMethodSelected={handlePaymentMethod}
            onUpdateAvailableWallets={handleUpdateAvailableWallets}
          />
        </>
      ) : null}
      <StripePaymentMethodForm
        variant={variant}
        buttonLabel={buttonLabel}
        onPaymentMethod={handlePaymentMethod}
        onError={(error) => setErrorMessage(error)}
        onLoading={(isLoading) => setIsLoading(isLoading)}
      >
        <CenterWrapper>
          {DonationCheckoutButton ? (
            <DonationCheckoutButton
              type="submit"
              label={buttonLabel || `Donate ${currencyCode} ${amount} now`}
              loading={isLoading}
            />
          ) : (
            <>
              <Checkbox
                useBuiltInMargins={false}
                checked={emailOptIn}
                label={emailOptInLabel}
                onChange={setEmailOptIn}
                defaultValue={true}
                variant="detail"
                color={colors.darkGray}
              />

              <PrimaryButton
                type="submit"
                variant="solid"
                label={buttonLabel || `Donate ${currencyCode} ${amount} now`}
                loading={isLoading}
                css={css`
                  margin: ${spacing[5]}px 0px ${spacing[4]}px;
                  width: 100%;
                  ${colorTheme === 'dark' &&
                  css`
                    color: ${colors.white};
                    background-color: ${colors.black};
                    border-color: ${colors.black};

                    :hover {
                      color: ${colors.white};
                      background-color: ${colors.black};
                      border-color: ${colors.black};
                    }
                  `}
                `}
              />
            </>
          )}
          {isVisible && (
            <TurnstileErrorBoundary>
              <Turnstile
                action="stripe-checkout"
                theme="light"
                ref={turnstile}
                onTokenSet={(token) => {
                  turnstileToken.current = token;
                }}
              />
            </TurnstileErrorBoundary>
          )}
          {variant === 'verbose' && (
            <>
              <Typography
                useBuiltInMargins
                variant="detail"
                color={colors.grey}
                css={css`
                  margin-top: ${spacing[3]}px;
                  text-align: center;
                `}
              >
                By making a donation, you agree to our{' '}
                <Link href="/terms-of-use">Terms of Use</Link> and{' '}
                <Link href="/privacy-policy">Privacy Policy</Link>
              </Typography>
              <GhostButton
                color={colors.darkGrey}
                onClick={() => {
                  Router.back();
                }}
                css={css`
                  margin-top: ${spacing[4]}px;
                `}
                label="Cancel"
              />
            </>
          )}
        </CenterWrapper>
      </StripePaymentMethodForm>
      {errorMessage && (
        <ErrorBlock>
          <Typography useBuiltInMargins variant="detail" tag="span" color={colors.red}>
            {errorMessage}
          </Typography>
        </ErrorBlock>
      )}
    </Elements>
  );
}

type LinkProps = {
  href?: string;
  color?: string;
  variant?: TypographyVariants;
  children?: string;
};

const Link = ({ href = '', color = colors.darkGrey, variant = 'detail', children }: LinkProps) => (
  <Typography
    useBuiltInMargins
    variant={variant}
    tag="a"
    href={href}
    css={css`
      color: ${color};
      text-decoration: underline;

      :hover {
        cursor: pointer;
      }
    `}
  >
    {children}
  </Typography>
);
